// Copyright 2025, University of Maryland, All Rights Reserved
import React, { ReactNode, createContext, useContext } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CostsComponent as CostsUI } from './components/costs/CostsComponent';
import { CostsComponentProps } from './types';
import { AxiosInstance } from 'axios';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create an API context to pass the API instance down the component tree
type ApiContextType = {
  apiInstance: AxiosInstance;
};

const ApiContext = createContext<ApiContextType | null>(null);

// Hook to use the API context
export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

interface AuthErrorHandlerProps {
  children: ReactNode;
}

// Create a wrapper component for auth handling
const AuthErrorHandler = ({ children }: AuthErrorHandlerProps) => {
  // Note: Auth error handling is now managed by the centralized API manager
  // The apiInstance passed from the parent already has error handling configured
  return <>{children}</>;
};

/**
 * Main Costs Component
 * Now accepts a pre-configured API instance instead of creating its own
 */
export const CostsComponent: React.FC<CostsComponentProps> = ({
  app_id,
  api_instance,
  organization_id,
  workspace_id,
  default_cost_view,
  is_admin,
  can_manage_workspace_costs,
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiContext.Provider value={{ apiInstance: api_instance }}>
        <AuthErrorHandler>
          <CostsUI
            organization_id={organization_id}
            workspace_id={workspace_id}
            app_id={app_id}
            defaultCostView={default_cost_view}
            isGlobalAdmin={is_admin}
            canManageWorkspaceCosts={can_manage_workspace_costs}
          />
        </AuthErrorHandler>
      </ApiContext.Provider>
    </QueryClientProvider>
  );
};
